import { useState, useEffect, useMemo } from 'react';
import { createVideoElementFromTracks } from '../../webrtc/utils';
import { useLocalMedia } from './useLocalMedia';

export type UseLocalMediaVideoElementCopyReturnType = {
  /**
   * Video element with the local media content.
   */
  videoElement: HTMLVideoElement | undefined;
  /**
   * Local media audio track
   */
  audioTrack: MediaStreamTrack | undefined;
  /**
   * Local media video track
   */
  videoTrack: MediaStreamTrack | undefined;
};

/**
 * Returns an entity duplicates of an existing local media.
 * @returns {UseLocalMediaVideoElementCopyReturnType}
 */
export function useLocalMediaVideoElementCopy(): UseLocalMediaVideoElementCopyReturnType {
  const videoElement = useMemo(() => createVideoElementFromTracks([]), []);
  const [audioTrack, setAudioTrack] = useState<MediaStreamTrack>();
  const [videoTrack, setVideoTrack] = useState<MediaStreamTrack>();

  const { tracks: originalTracks } = useLocalMedia();

  useEffect(() => {
    const stream = videoElement.srcObject as MediaStream;
    const existingTracks = stream.getTracks();
    // remove old tracks
    for (const track of existingTracks) {
      const isOriginalTwinRemoved = !originalTracks.some(
        (t) => t.id === track.id,
      );
      if (isOriginalTwinRemoved) {
        stream.removeTrack(track);
      }
    }
    // add new tracks
    for (const originalTrack of originalTracks) {
      const isOriginalTrackAdded = !existingTracks.some(
        (t) => t.id === originalTrack.id,
      );
      if (isOriginalTrackAdded) {
        const track = originalTrack;
        stream.addTrack(track);
      }
    }
    setAudioTrack(stream.getAudioTracks()[0]);
    setVideoTrack(stream.getVideoTracks()[0]);

    if (videoElement.paused) {
      videoElement.play().catch(() => undefined);
    }
  }, [originalTracks, videoElement]);

  return { videoElement, audioTrack, videoTrack };
}
